// Hook
import useLocations from '@/hooks/useLocations'

// UI
import ComponentInvlevelInList from './ComponentInvlevelInList'
import ComponentInventoryStatus from './ComponentInventoryStatus'
import ComponentHoldQuantity from './ComponentHoldQuantity'
import QuantityDisplay from '@/components/ui/QuantityDisplay'

import {
    IconHistory,
    IconMapPinFilled,
    IconAlertTriangle,
    IconCheck,
} from '@tabler/icons-react'
import Box from '@/components/ui/Box'

// Dayjs
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

// Mantine
import { Anchor, Button } from '@mantine/core'
import { notifications } from '@mantine/notifications'

// Hooks
import useUserLocation from '@/hooks/useUserLocation'

// Classnames
import classNames from 'classnames'

// User
import useMe from '@/hooks/useMe'

// Api
import { useAxiosApi } from '@/hooks/useAxiosApi'

// React
import { useState } from 'react'

// Lodash
import { sumBy } from 'lodash'

// Initialize dayjs plugins
dayjs.extend(relativeTime)

export default function ComponentInventoryDetail({ component, loadComponent }) {
    // User
    const { isAdmin } = useMe()

    const locations = useLocations()

    const { userLocationId } = useUserLocation()

    const elements = locations
        .map((location) => {
            const physicalInvlevel = component.invlevels.find(
                (e) => e.location_id === location.id && e.invtype === 'physical'
            )

            // Last physical inventory
            const lastPhysicalInventory =
                physicalInvlevel?.last_inventory ?? null

            const lastPhysicalInventoryDays = lastPhysicalInventory
                ? dayjs().diff(dayjs(lastPhysicalInventory), 'days')
                : null

            const inventoryDaysLasting =
                lastPhysicalInventoryDays != null &&
                component.inventory_cycle_days
                    ? lastPhysicalInventoryDays - component.inventory_cycle_days
                    : null

            const hold = component.holds.find((item) => item.location_id === location.id)
            const holdQuantity = hold?.hold_quantity ?? 0

            return {
                id: location.id,
                name: location.name,
                physicalInvlevel: physicalInvlevel?.invlevel ?? null,
                lastPhysicalInventoryDays,
                lastPhysicalInventory,
                inventoryDaysLasting,
                holdQuantity,
                holdBreakdown: hold?.breakdown,
                availableQuantity:
                    (physicalInvlevel?.invlevel ?? 0) - holdQuantity,
            }
        })
        .filter((e) => isAdmin || e.id === 1)

    const rows = elements.map((e) => {
        const needs_inventory =
            e.physicalInvlevel !== null &&
            component.inventory_cycle_days !== null

        // Check if the location is the current location
        const isCurrentLocation = e.id === userLocationId

        // Add a blue background to the current location row
        const rowClasses = classNames(
            'hover:bg-gray-25 border-b border-gray-100',
            {
                'bg-blue-50': isCurrentLocation,
            }
        )

        const inventoryHoverClasses = classNames('relative', {
            'hover:bg-gray-50': !isCurrentLocation,
            'hover:bg-blue-100': isCurrentLocation,
        })

        return (
            <tr key={e.id} className={rowClasses}>
                <td className="py-3 px-4 flex items-center justify-between truncate">
                    <Anchor
                        size="sm"
                        href={`/components?physical_inventory=with&location_id=${e.id}`}
                    >
                        <span className="truncate">{e.name}</span>
                    </Anchor>

                    {isCurrentLocation && (
                        <IconMapPinFilled className="text-baltic" size="1rem" />
                    )}
                </td>

                <td className={inventoryHoverClasses}>
                    <div className="absolute inset-0">
                        <ComponentInvlevelInList
                            component={component}
                            invlevel={e.physicalInvlevel}
                            location_id={e.id}
                            location_name={e.name}
                            onInvlevelChange={() => loadComponent()}
                        />
                    </div>
                </td>

                <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                        <ComponentInventoryStatus
                            last_inventory_days={e.inventoryDaysLasting}
                            last_inventory_date={e.lastPhysicalInventory}
                            needs_inventory={needs_inventory}
                        />
                    </div>
                </td>

                <td className="py-3 px-4 text-sm">
                    {e.holdQuantity > 0 ? (
                        <div className="-my-3 flex justify-center">
                            <ComponentHoldQuantity quantity={e.holdQuantity} breakdown={e.holdBreakdown} locationName={e.name} />
                        </div>
                    ) : (
                        <span className=" text-gray-400 text-center flex items-center justify-center">
                            -
                        </span>
                    )}
                </td>

                <td className="py-3 px-4 text-sm flex items-center justify-end">
                    <QuantityDisplay
                        quantity={e.availableQuantity || 0}
                        greenWhenPositive={true}
                    />
                </td>
            </tr>
        )
    })

    // Recalculate holds
    const { requestPost } = useAxiosApi()
    const [isComputingHolds, setIsComputingHolds] = useState(false)

    const recalculateHolds = async () => {
        setIsComputingHolds(true)

        await requestPost(`/components/${component.id}/compute-holds`)

        notifications.show({
            title: 'Recalcul effectué',
            color: 'green',
        })

        await loadComponent()

        setIsComputingHolds(false)
    }

    // Calculate global available quantity

    const fulfillableInvlevel = sumBy(component.invlevels, (i) =>
        i.location.can_fulfill_orders === true && i.invtype === 'physical'
            ? i.invlevel
            : 0
    )

    const holdQuantity = sumBy(component.holds, (hold) => hold.hold_quantity)

    const availableQuantity = fulfillableInvlevel - holdQuantity

    return (
        <div>
            {/* Main Table */}
            <div className="bg-white border rounded-2xl overflow-hidden flex flex-col">
                <div>
                    <table className="min-w-full divide-y">
                        <thead className="bg-white text-black">
                            <tr>
                                <th className="py-4 px-4 text-left font-medium text-sm tracking-wider">
                                    Emplacement
                                </th>
                                <th className="py-4 px-4 text-left font-medium text-sm tracking-wider">
                                    Physique
                                </th>
                                <th className="py-4 px-4 text-left font-medium text-sm tracking-wider">
                                    Inventaire
                                </th>
                                <th className="py-4 px-4 text-left font-medium text-sm tracking-wider">
                                    <div className="flex items-center gap-2">
                                        Réservés
                                    </div>
                                </th>

                                <th className="py-4 px-4 text-left font-medium text-sm tracking-wider">
                                    <div className="flex items-center gap-2">
                                        Disponible
                                    </div>
                                </th>
                            </tr>
                        </thead>

                        <tbody className="bg-white divide-y divide-gray-100">
                            {rows}
                        </tbody>
                    </table>
                </div>

                <div className="border-t px-4 py-2 text-xs text-gray-400 flex justify-between items-center">
                    <span>
                        Dernier calcul des réservations :{' '}
                        {component.last_holds_computed_at
                            ? dayjs(component.last_holds_computed_at).fromNow()
                            : 'Jamais'}
                    </span>

                    <Button
                        variant="default"
                        size="xs"
                        onClick={recalculateHolds}
                        loading={isComputingHolds}
                    >
                        Recalculer les réservés
                    </Button>
                </div>
            </div>
        </div>
    )
}
